import { useNavigate } from "react-router-dom";
import { CheckCircle2, Clock, AlertTriangle, XCircle, MinusCircle } from "lucide-react";
import { fmtDateTime } from "@/lib/api";
import { cn } from "@/lib/utils";

const fmtTime = (iso) => (iso ? new Date(iso).toLocaleTimeString("en-US", { timeZone: "America/New_York", hour: "numeric", minute: "2-digit" }) + " ET" : "");

function rowState(f, now) {
  if (!f.schedule?.enabled) return { tone: "muted", Icon: MinusCircle, word: "Not scheduled", note: "No daily delivery expected for this report" };
  const slot = f.received_for_slot;
  switch (f.schedule_status) {
    case "received": return { tone: "ok", Icon: CheckCircle2, word: `Arrived ${fmtTime(slot?.received_at)}`, note: slot?.status === "duplicate" ? "Identical to the previous file — no new data" : `${slot?.row_counts?.detail_rows ?? "—"} rows published` };
    case "processing": return { tone: "info", Icon: Clock, word: `Arrived ${fmtTime(slot?.received_at)}`, note: "Checking and publishing now" };
    case "incomplete": return { tone: "bad", Icon: AlertTriangle, word: "Arrived but held", note: `${slot?.status || "quarantined"} by safety checks — needs a look in Imports` };
    case "awaiting": return { tone: "wait", Icon: Clock, word: "Waiting", note: `Due by ${fmtTime(f.deadline_at)} — alert goes out if still missing` };
    case "missed": return { tone: "bad", Icon: XCircle, word: f.escalate_at && now >= new Date(f.escalate_at) ? "Still missing — escalated" : "Missing — alert sent", note: `Expected ${fmtTime(f.expected_at)}; nobody has received today's file` };
    default: return { tone: "muted", Icon: MinusCircle, word: "Unknown", note: "Watchdog has not evaluated this report yet" };
  }
}

const ROW = { ok: "text-emerald-700", info: "text-sky-700", wait: "text-amber-700", bad: "text-rose-700", muted: "text-slate-500" };

export function MorningStatusCard({ data, isAdmin }) {
  const navigate = useNavigate();
  const now = new Date();
  const rows = data.feeds.map((f) => ({ f, s: rowState(f, now) }));
  const scheduled = rows.filter((r) => r.f.schedule?.enabled);
  const arrived = scheduled.filter((r) => ["received", "processing"].includes(r.f.schedule_status)).length;
  const bad = scheduled.filter((r) => r.s.tone === "bad").length;
  const total = scheduled.length;
  const headline = total === 0 ? "No reports are scheduled" : arrived === total ? `Yes — all ${total} arrived` : bad ? `No — ${bad} need${bad === 1 ? "s" : ""} attention` : arrived === 0 ? "Not yet — waiting for today's reports" : `${arrived} of ${total} arrived so far`;
  const tone = total === 0 ? "border-slate-200" : arrived === total ? "border-emerald-300 bg-emerald-50/40" : bad ? "border-rose-300 bg-rose-50/40" : "border-amber-200 bg-amber-50/30";
  const next = rows.map((r) => r.f.next_expected_at).filter(Boolean).sort()[0];
  return (
    <section className={cn("tqi-card border-2 p-5", tone)} data-testid="morning-status-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="tqi-eyebrow">Today {data.today_ny} (ET)</div>
          <h2 className="mt-0.5 text-base font-bold text-[#101C47] md:text-lg">Did today's {total || 5} reports arrive?</h2>
          <p className="mt-1 text-2xl font-extrabold tracking-tight text-slate-900 md:text-3xl" data-testid="morning-status-headline">{headline}</p>
        </div>
        <div className="text-right text-xs text-slate-600">
          <div>EZLynx sends them around 6:00 AM ET every day.</div>
          {next && <div data-testid="morning-status-next">Next expected {fmtDateTime(next)}</div>}
        </div>
      </div>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2 xl:grid-cols-5" data-testid="morning-status-list">
        {rows.map(({ f, s }) => (
          <li key={f.feed} className="flex items-start gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2.5" data-testid={`morning-status-${f.feed}`}>
            <s.Icon className={cn("mt-0.5 h-4 w-4 shrink-0", ROW[s.tone])} />
            <div className="min-w-0">
              <div className="text-sm font-semibold text-slate-900">{f.label}</div>
              <div className={cn("text-xs font-semibold", ROW[s.tone])} data-testid={`morning-status-word-${f.feed}`}>{s.word}</div>
              <div className="text-[11px] text-slate-500">{s.note}</div>
            </div>
          </li>
        ))}
      </ul>
      {isAdmin && bad > 0 && <button className="mt-3 text-xs font-semibold text-[#101C47] hover:text-[#F92626]" onClick={() => navigate("/imports")} data-testid="morning-status-open-imports">Open Imports to review →</button>}
    </section>
  );
}
